import React, { useState, useEffect } from 'react';
import { TrendingUp, TrendingDown, AlertTriangle, DollarSign, Activity, RefreshCw, BarChart3, Eye, Calculator, Euro, Calendar, ArrowUpDown } from 'lucide-react';

const CryptoDashboard = () => {
  const [marketData, setMarketData] = useState([]);
  const [watchlistData, setWatchlistData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [activeTab, setActiveTab] = useState('market'); // 'market', 'watchlist', 'simulator'
  
  // Simulator states
  const [simulatorCrypto, setSimulatorCrypto] = useState('bitcoin');
  const [investmentAmount, setInvestmentAmount] = useState(1000);
  const [daysAgo, setDaysAgo] = useState(365);
  const [simulationResult, setSimulationResult] = useState(null);
  const [simLoading, setSimLoading] = useState(false);

  // Watchlist crypto (le tue crypto di interesse)
  const watchlistCoins = [
    { id: 'ethereum', symbol: 'ETH', name: 'Ethereum' },
    { id: 'crypto-com-chain', symbol: 'CRO', name: 'Cronos' },
    { id: 'polkadot', symbol: 'DOT', name: 'Polkadot' },
    { id: 'dogwifcoin', symbol: 'WIF', name: 'dogwifhat' },
    { id: 'dogecoin', symbol: 'DOGE', name: 'Dogecoin' },
    { id: 'solana', symbol: 'SOL', name: 'Solana' },
    { id: 'synapse-2', symbol: 'SYN', name: 'Synapse' }
  ];

  // Lista crypto per il simulatore
  const simulatorOptions = [
    { id: 'bitcoin', name: 'Bitcoin (BTC)' },
    { id: 'ethereum', name: 'Ethereum (ETH)' },
    { id: 'binancecoin', name: 'BNB (BNB)' },
    { id: 'solana', name: 'Solana (SOL)' },
    { id: 'cardano', name: 'Cardano (ADA)' },
    { id: 'dogecoin', name: 'Dogecoin (DOGE)' },
    { id: 'polkadot', name: 'Polkadot (DOT)' },
    { id: 'chainlink', name: 'Chainlink (LINK)' },
    { id: 'avalanche-2', name: 'Avalanche (AVAX)' },
    { id: 'polygon', name: 'Polygon (MATIC)' }
  ];

  // Algoritmo AI per risk assessment
  const calculateRiskLevel = (priceChange24h, volume24h, marketCap) => {
    const volatility = Math.abs(priceChange24h);
    const volumeRatio = volume24h / marketCap;
    
    if (volatility > 15 || volumeRatio > 0.3) return { level: 'Very High', color: 'text-red-600 bg-red-100' };
    if (volatility > 10 || volumeRatio > 0.2) return { level: 'High', color: 'text-orange-600 bg-orange-100' };
    if (volatility > 5 || volumeRatio > 0.1) return { level: 'Medium', color: 'text-yellow-600 bg-yellow-100' };
    return { level: 'Low', color: 'text-green-600 bg-green-100' };
  };

  // Predizione AI
  const generateAIPrediction = (priceChange24h, volume24h, marketCap) => {
    const trend = priceChange24h;
    const volumeRatio = volume24h / marketCap;
    const momentum = trend * (1 + volumeRatio);
    
    if (momentum > 8) return { prediction: 'Bullish', color: 'text-green-600 bg-green-100', icon: TrendingUp };
    if (momentum < -8) return { prediction: 'Bearish', color: 'text-red-600 bg-red-100', icon: TrendingDown };
    return { prediction: 'Neutral', color: 'text-gray-600 bg-gray-100', icon: Activity };
  };

  // Algoritmo AI avanzato per target realistici
  const calculateTargets = (currentPrice, priceChange24h, marketCap, volume24h, marketCapRank) => {
    const volatility = Math.abs(priceChange24h);
    const volumeRatio = volume24h / marketCap;
    const trend = priceChange24h;
    
    // Fattori per calcolo target intelligente
    let multiplier = 1;
    
    // Rank influence (crypto più grandi = target più conservativi)
    if (marketCapRank <= 10) multiplier = 0.15; // Top 10: +15% target
    else if (marketCapRank <= 50) multiplier = 0.25; // Top 50: +25%
    else if (marketCapRank <= 200) multiplier = 0.45; // Top 200: +45%
    else multiplier = 0.8; // Altri: +80%
    
    // Aggiustamento per trend
    if (trend > 10) multiplier *= 1.3; // Trend forte positivo
    else if (trend > 5) multiplier *= 1.15; // Trend positivo
    else if (trend < -10) multiplier *= 0.7; // Trend forte negativo
    else if (trend < -5) multiplier *= 0.85; // Trend negativo
    
    // Aggiustamento per volume (alta liquidità = target più vicini)
    if (volumeRatio > 0.3) multiplier *= 0.8;
    else if (volumeRatio < 0.05) multiplier *= 1.2;
    
    // Calcolo finale
    const targetPrice = currentPrice * (1 + multiplier);
    const stopLoss = currentPrice * (1 - (multiplier * 0.6)); // Stop loss più vicino
    
    return { targetPrice, stopLoss };
  };

  // Enrichment dei dati con AI
  const enrichCryptoData = (data) => {
    return data.map(coin => {
      const risk = calculateRiskLevel(
        coin.price_change_percentage_24h || 0,
        coin.total_volume || 0,
        coin.market_cap || 0
      );
      
      const aiPrediction = generateAIPrediction(
        coin.price_change_percentage_24h || 0,
        coin.total_volume || 0,
        coin.market_cap || 0
      );
      
      const targets = calculateTargets(
        coin.current_price,
        coin.price_change_percentage_24h || 0
      );
      
      return {
        ...coin,
        riskLevel: risk,
        aiPrediction: aiPrediction,
        targetPrice: targets.targetPrice,
        stopLoss: targets.stopLoss
      };
    });
  };

  // Fetch Top 10 Market
  const fetchMarketData = async () => {
    try {
      const response = await fetch(
        'https://api.coingecko.com/api/v3/coins/markets?vs_currency=eur&order=market_cap_desc&per_page=10&page=1&sparkline=false&price_change_percentage=24h'
      );
      
      if (!response.ok) throw new Error('Errore nel caricamento del market');
      
      const data = await response.json();
      const enrichedData = enrichCryptoData(data);
      setMarketData(enrichedData);
    } catch (error) {
      console.error('Errore market data:', error);
    }
  };

  // Fetch Watchlist
  const fetchWatchlistData = async () => {
    try {
      const coinIds = watchlistCoins.map(coin => coin.id).join(',');
      
      const response = await fetch(
        `https://api.coingecko.com/api/v3/coins/markets?vs_currency=eur&ids=${coinIds}&order=market_cap_desc&per_page=10&page=1&sparkline=false&price_change_percentage=24h`
      );
      
      if (!response.ok) throw new Error('Errore nel caricamento della watchlist');
      
      const data = await response.json();
      const enrichedData = enrichCryptoData(data);
      setWatchlistData(enrichedData);
    } catch (error) {
      console.error('Errore watchlist data:', error);
    }
  };

  // Simulatore di prezzo (versione semplificata)
  const runSimulation = async () => {
    try {
      setSimLoading(true);
      
      // Fetch dati crypto correnti
      const currentResponse = await fetch(
        `https://api.coingecko.com/api/v3/coins/${simulatorCrypto}?localization=false&tickers=false&market_data=true&community_data=false&developer_data=false`
      );
      
      if (!currentResponse.ok) {
        throw new Error('Errore nel caricamento dei dati');
      }
      
      const currentData = await currentResponse.json();
      const currentPrice = currentData.market_data?.current_price?.eur || 0;
      
      if (currentPrice === 0) {
        throw new Error('Prezzo corrente non disponibile');
      }
      
      // Simulazione basata su variazione percentuale media
      let expectedChange = 0;
      
      // Stima variazione basata su timeframe (semplificata)
      if (daysAgo === 7) expectedChange = -5 + Math.random() * 30; // ±15% settimana
      else if (daysAgo === 30) expectedChange = -15 + Math.random() * 60; // ±30% mese
      else if (daysAgo === 90) expectedChange = -25 + Math.random() * 100; // ±50% 3 mesi
      else if (daysAgo === 180) expectedChange = -40 + Math.random() * 150; // ±75% 6 mesi
      else if (daysAgo === 365) expectedChange = -50 + Math.random() * 300; // ±150% anno
      else if (daysAgo === 730) expectedChange = -70 + Math.random() * 500; // ±250% 2 anni
      
      // Aggiustamento per crypto specifiche (Bitcoin più stabile, altcoin più volatili)
      if (simulatorCrypto === 'bitcoin') expectedChange *= 0.7;
      else if (simulatorCrypto === 'ethereum') expectedChange *= 0.8;
      else if (['dogecoin', 'cardano'].includes(simulatorCrypto)) expectedChange *= 1.3;
      
      const estimatedOldPrice = currentPrice / (1 + expectedChange / 100);
      const coinsOwned = investmentAmount / estimatedOldPrice;
      const currentValue = coinsOwned * currentPrice;
      const profit = currentValue - investmentAmount;
      const profitPercentage = ((currentValue - investmentAmount) / investmentAmount) * 100;
      
      setSimulationResult({
        cryptoName: currentData.name,
        cryptoSymbol: currentData.symbol.toUpperCase(),
        initialInvestment: investmentAmount,
        oldPrice: estimatedOldPrice,
        currentPrice: currentPrice,
        coinsOwned: coinsOwned,
        currentValue: currentValue,
        profit: profit,
        profitPercentage: profitPercentage,
        daysAgo: daysAgo,
        isEstimated: true
      });
      
    } catch (error) {
      console.error('Errore simulazione:', error);
      setSimulationResult({ error: error.message });
    } finally {
      setSimLoading(false);
    }
  };

  // Helper per formattare data
  const getDateString = (daysAgo) => {
    const date = new Date();
    date.setDate(date.getDate() - daysAgo);
    return date.toISOString().split('T')[0].split('-').reverse().join('-');
  };

  // Fetch completo
  const fetchAllData = async () => {
    try {
      setLoading(true);
      await Promise.all([fetchMarketData(), fetchWatchlistData()]);
      setLastUpdate(new Date());
    } catch (error) {
      console.error('Errore:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllData();
    
    // Auto-refresh ogni 60 secondi
    const interval = setInterval(fetchAllData, 60000);
    return () => clearInterval(interval);
  }, []);

  const formatPrice = (price) => {
    if (price < 0.01) return `€${price.toFixed(6)}`;
    if (price < 1) return `€${price.toFixed(4)}`;
    return `€${price.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatMarketCap = (marketCap) => {
    if (marketCap >= 1e9) return `€${(marketCap / 1e9).toFixed(2)}B`;
    if (marketCap >= 1e6) return `€${(marketCap / 1e6).toFixed(2)}M`;
    return `€${marketCap.toLocaleString('it-IT')}`;
  };

  // Componente Card Crypto
  const CryptoCard = ({ crypto, showRank = true }) => {
    const PredictionIcon = crypto.aiPrediction.icon;
    const isPositive = crypto.price_change_percentage_24h >= 0;
    
    return (
      <div className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 p-6 border border-gray-200">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <img 
              src={crypto.image} 
              alt={crypto.name}
              className="w-10 h-10 rounded-full"
            />
            <div>
              <h3 className="font-bold text-lg text-gray-800">{crypto.symbol.toUpperCase()}</h3>
              <p className="text-sm text-gray-500">{crypto.name}</p>
            </div>
          </div>
          {showRank && (
            <div className="text-right">
              <span className="text-sm text-gray-500">#{crypto.market_cap_rank}</span>
            </div>
          )}
        </div>

        <div className="mb-4">
          <div className="text-2xl font-bold text-gray-800 mb-1">
            {formatPrice(crypto.current_price)}
          </div>
          <div className={`flex items-center gap-1 text-sm font-medium ${
            isPositive ? 'text-green-600' : 'text-red-600'
          }`}>
            {isPositive ? (
              <TrendingUp className="w-4 h-4" />
            ) : (
              <TrendingDown className="w-4 h-4" />
            )}
            {crypto.price_change_percentage_24h?.toFixed(2)}%
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 mb-4 text-sm">
          <div>
            <span className="text-gray-500">Market Cap</span>
            <div className="font-semibold">{formatMarketCap(crypto.market_cap)}</div>
          </div>
          <div>
            <span className="text-gray-500">Volume 24h</span>
            <div className="font-semibold">{formatMarketCap(crypto.total_volume)}</div>
          </div>
        </div>

        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600">Risk Level</span>
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${crypto.riskLevel.color}`}>
              {crypto.riskLevel.level}
            </span>
          </div>

          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600">AI Prediction</span>
            <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${crypto.aiPrediction.color}`}>
              <PredictionIcon className="w-3 h-3" />
              {crypto.aiPrediction.prediction}
            </div>
          </div>

          <div className="pt-2 border-t border-gray-100">
            <div className="flex justify-between text-xs text-gray-600 mb-1">
              <span>Target: {formatPrice(crypto.targetPrice)}</span>
              <span>Stop Loss: {formatPrice(crypto.stopLoss)}</span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Componente Simulatore
  const SimulatorComponent = () => (
    <div className="max-w-4xl mx-auto">
      <div className="bg-white rounded-xl shadow-lg p-8 border border-gray-200">
        <h3 className="text-xl font-bold text-gray-800 mb-6 flex items-center gap-2">
          <Calculator className="w-6 h-6 text-blue-600" />
          Simulatore "What If"
        </h3>
        
        <div className="grid md:grid-cols-2 gap-8">
          {/* Input Section */}
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Cryptocurrency
              </label>
              <select
                value={simulatorCrypto}
                onChange={(e) => setSimulatorCrypto(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {simulatorOptions.map(option => (
                  <option key={option.id} value={option.id}>
                    {option.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Importo investimento (€)
              </label>
              <input
                type="number"
                value={investmentAmount}
                onChange={(e) => setInvestmentAmount(Number(e.target.value))}
                min="1"
                max="1000000"
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="1000"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Giorni fa
              </label>
              <select
                value={daysAgo}
                onChange={(e) => setDaysAgo(Number(e.target.value))}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value={7}>1 settimana fa</option>
                <option value={30}>1 mese fa</option>
                <option value={90}>3 mesi fa</option>
                <option value={180}>6 mesi fa</option>
                <option value={365}>1 anno fa</option>
                <option value={730}>2 anni fa</option>
              </select>
            </div>

            <button
              onClick={runSimulation}
              disabled={simLoading}
              className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg hover:bg-blue-700 transition-colors font-medium flex items-center justify-center gap-2"
            >
              {simLoading ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <Calculator className="w-4 h-4" />
              )}
              {simLoading ? 'Calcolando...' : 'Simula Investimento'}
            </button>
          </div>

          {/* Results Section */}
          <div>
            {simulationResult && !simulationResult.error && (
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-6 border border-blue-200">
                <h4 className="font-bold text-lg text-gray-800 mb-4 flex items-center gap-2">
                  <ArrowUpDown className="w-5 h-5 text-blue-600" />
                  Risultato Simulazione
                </h4>
                
                <div className="space-y-4">
                  <div className="text-center">
                    <p className="text-sm text-gray-600 mb-1">
                      Se avessi investito €{simulationResult.initialInvestment.toLocaleString('it-IT')} in {simulationResult.cryptoName}
                    </p>
                    <p className="text-sm text-gray-600 mb-3">
                      {simulationResult.daysAgo} giorni fa... {simulationResult.isEstimated && '(stima)'}
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div className="bg-white p-3 rounded-lg">
                      <p className="text-gray-500">Prezzo stimato allora</p>
                      <p className="font-bold">{formatPrice(simulationResult.oldPrice)}</p>
                    </div>
                    <div className="bg-white p-3 rounded-lg">
                      <p className="text-gray-500">Prezzo ora</p>
                      <p className="font-bold">{formatPrice(simulationResult.currentPrice)}</p>
                    </div>
                  </div>

                  <div className="bg-white p-4 rounded-lg">
                    <p className="text-gray-500 text-sm mb-1">Coin possedute</p>
                    <p className="font-bold">{simulationResult.coinsOwned.toFixed(6)} {simulationResult.cryptoSymbol}</p>
                  </div>

                  <div className="bg-white p-4 rounded-lg">
                    <p className="text-gray-500 text-sm mb-1">Valore attuale</p>
                    <p className="font-bold text-lg">€{simulationResult.currentValue.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                  </div>

                  <div className={`p-4 rounded-lg ${simulationResult.profit >= 0 ? 'bg-green-100 border border-green-200' : 'bg-red-100 border border-red-200'}`}>
                    <p className="text-sm text-gray-600 mb-1">Profitto/Perdita</p>
                    <p className={`font-bold text-xl ${simulationResult.profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {simulationResult.profit >= 0 ? '+' : ''}€{simulationResult.profit.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </p>
                    <p className={`font-medium ${simulationResult.profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      ({simulationResult.profitPercentage >= 0 ? '+' : ''}{simulationResult.profitPercentage.toFixed(2)}%)
                    </p>
                  </div>
                </div>
              </div>
            )}

            {simulationResult && simulationResult.error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                <p className="text-red-600 font-medium">Errore nella simulazione</p>
                <p className="text-red-500 text-sm mt-1">{simulationResult.error}</p>
              </div>
            )}

            {!simulationResult && (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
                <Calculator className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-500">Seleziona i parametri e clicca "Simula Investimento" per vedere i risultati</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  if (loading && marketData.length === 0 && watchlistData.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center py-20">
            <RefreshCw className="w-12 h-12 animate-spin mx-auto text-blue-600 mb-4" />
            <h2 className="text-2xl font-bold text-gray-800">Caricamento Dashboard...</h2>
          </div>
        </div>
      </div>
    );
  }

  const getCurrentData = () => {
    if (activeTab === 'market') return marketData;
    if (activeTab === 'watchlist') return watchlistData;
    return [];
  };

  const currentData = getCurrentData();

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-800 mb-2">
            Crypto AI Dashboard
          </h1>
          <p className="text-gray-600 mb-6">Analisi Avanzata con Intelligenza Artificiale</p>
          
          {/* Tab Navigation */}
          <div className="flex justify-center mb-6">
            <div className="bg-white rounded-lg p-1 shadow-md border border-gray-200">
              <button
                onClick={() => setActiveTab('market')}
                className={`flex items-center gap-2 px-4 py-3 rounded-md transition-all duration-200 font-medium ${
                  activeTab === 'market'
                    ? 'bg-blue-600 text-white shadow-md'
                    : 'text-gray-600 hover:text-blue-600 hover:bg-blue-50'
                }`}
              >
                <BarChart3 className="w-4 h-4" />
                Market Overview
              </button>
              <button
                onClick={() => setActiveTab('watchlist')}
                className={`flex items-center gap-2 px-4 py-3 rounded-md transition-all duration-200 font-medium ${
                  activeTab === 'watchlist'
                    ? 'bg-blue-600 text-white shadow-md'
                    : 'text-gray-600 hover:text-blue-600 hover:bg-blue-50'
                }`}
              >
                <Eye className="w-4 h-4" />
                Selected Coins
              </button>
              <button
                onClick={() => setActiveTab('simulator')}
                className={`flex items-center gap-2 px-4 py-3 rounded-md transition-all duration-200 font-medium ${
                  activeTab === 'simulator'
                    ? 'bg-blue-600 text-white shadow-md'
                    : 'text-gray-600 hover:text-blue-600 hover:bg-blue-50'
                }`}
              >
                <Calculator className="w-4 h-4" />
                Price Simulator
              </button>
            </div>
          </div>
          
          {/* Status e Refresh */}
          {activeTab !== 'simulator' && (
            <div className="flex items-center justify-center gap-4 text-sm text-gray-500">
              <span>Ultimo aggiornamento: {lastUpdate?.toLocaleTimeString('it-IT')}</span>
              <button 
                onClick={fetchAllData}
                className="flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200 transition-colors"
                disabled={loading}
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                Aggiorna
              </button>
            </div>
          )}
        </div>

        {/* Content */}
        {activeTab === 'simulator' ? (
          <SimulatorComponent />
        ) : (
          <>
            {/* Content Header */}
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-gray-800 mb-2">
                {activeTab === 'market' ? '🌍 Top 10 Cryptocurrency' : '👁️ Selected Coins'}
              </h2>
              <p className="text-gray-600">
                {activeTab === 'market' 
                  ? `Le ${currentData.length} cryptocurrency più capitalizzate al mondo`
                  : `${currentData.length} crypto selezionate con analisi AI avanzata`
                }
              </p>
            </div>

            {/* Crypto Cards Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
              {currentData.map((crypto) => (
                <CryptoCard 
                  key={crypto.id} 
                  crypto={crypto} 
                  showRank={activeTab === 'market'} 
                />
              ))}
            </div>
          </>
        )}

        {/* Footer */}
        <div className="text-center mt-12 text-gray-500 text-sm">
          <p>Dati forniti da CoinGecko API • Aggiornamento automatico ogni 60 secondi</p>
          <p className="mt-1">⚠️ Le predizioni AI e simulazioni sono solo indicative e non costituiscono consigli di investimento</p>
        </div>
      </div>
    </div>
  );
};

export default CryptoDashboard;
